import type {
  CornerRadiusModifierValue,
  OpacityModifierValue,
} from "schemas/generated/fileModifiers";

import React, { useState } from "react";

import DirectionUnified from "@svg/modifiers/direction/direction-unified";
import TopLeftCorner from "@svg/modifiers/direction/top-left-corner";

import IconButton from "@replo/design-system/components/button/IconButton";

import CornerRadiusModifier from "./CornerRadiusModifier";
import OpacityModifier from "./OpacityModifier";

interface AppearanceModifierProps {
  onChange: (
    modifiers: (OpacityModifierValue | CornerRadiusModifierValue)[],
  ) => void;
}

const AppearanceModifier: React.FC<AppearanceModifierProps> = ({
  onChange,
}) => {
  const [isIndividualCorners, setIsIndividualCorners] = useState(false);

  const handleOpacityChange = React.useCallback(
    (modifier: OpacityModifierValue) => {
      onChange([modifier]);
    },
    [onChange],
  );

  const handleCornerRadiusChange = React.useCallback(
    (modifiers: CornerRadiusModifierValue[]) => {
      onChange(modifiers);
    },
    [onChange],
  );

  const handleCornerModeChange = React.useCallback((isIndividual: boolean) => {
    setIsIndividualCorners(isIndividual);
  }, []);

  const handleCornerModeToggle = React.useCallback(() => {
    handleCornerModeChange(!isIndividualCorners);
  }, [isIndividualCorners, handleCornerModeChange]);

  return (
    <div className="appearance-modifier">
      <div className="grid grid-cols-[1fr_1fr_min-content] gap-2 items-end">
        <div>
          <span className="text-xs font-medium text-muted">Opacity</span>
          <OpacityModifier onChange={handleOpacityChange} />
        </div>
        <div>
          <span className="text-xs font-medium text-muted">Corner Radius</span>
          <CornerRadiusModifier
            onChange={handleCornerRadiusChange}
            onModeChange={handleCornerModeChange}
            showButton={false}
            mode={isIndividualCorners ? "individual" : "unified"}
            showIndividualInputs={isIndividualCorners}
          />
        </div>
        <div>
          <IconButton
            variant="secondary"
            size="sm"
            onClick={handleCornerModeToggle}
            icon={
              isIndividualCorners ? (
                <DirectionUnified className="w-3 h-3" />
              ) : (
                <TopLeftCorner className="w-3 h-3" />
              )
            }
            tooltipText={
              isIndividualCorners
                ? "Set unified corners"
                : "Set individual corners"
            }
          />
        </div>
      </div>
      {isIndividualCorners && (
        <div className="mt-2 grid grid-cols-[1fr_min-content] gap-8">
          <div>
            <CornerRadiusModifier
              onChange={handleCornerRadiusChange}
              onModeChange={handleCornerModeChange}
              showButton={false}
              mode="individual"
              onlyShowIndividualInputs={true}
            />
          </div>
          <div></div>
        </div>
      )}
    </div>
  );
};

export default AppearanceModifier;
